import React from 'react';
import { Badge } from 'react-bootstrap';
import { FaArrowLeft, FaMapMarkerAlt, FaStar, FaDollarSign, FaCalendarPlus, FaRoute } from 'react-icons/fa';
import {
    FaPizzaSlice,
    FaCoffee,
    FaUtensils,
    FaDumbbell,
    FaTree,
    FaPills,
    FaHospital,
    FaGasPump,
    FaHotel,
    FaUniversity,
    FaStore,
    FaShoppingBag,
} from 'react-icons/fa';

/**
 * Inline place detail view shown when a place is selected.
 * Follows the PropertyDetail pattern — replaces the list in the side panel.
 */
function PlaceDetail({ item, onBack, onAddToCalendar }) {
    if (!item) return null;

    const cats = item.categoryNames || item.types || [];
    const rating = item.rating != null ? Number(item.rating).toFixed(1) : null;
    const priceLevel = item.price || item.priceLevel;
    const rawLatitude = item.location?.latitude ?? item.location?.lat;
    const rawLongitude = item.location?.longitude ?? item.location?.lng;
    const latitude = rawLatitude == null || rawLatitude === '' ? NaN : Number(rawLatitude);
    const longitude = rawLongitude == null || rawLongitude === '' ? NaN : Number(rawLongitude);
    const destination =
        Number.isFinite(latitude) && Number.isFinite(longitude)
            ? `${latitude},${longitude}`
            : item.formattedAddress || item.name || '';

    // Category icon
    const categoryIcon = (() => {
        const catStr = cats.join(' ').toLowerCase();
        if (/pizza/.test(catStr)) return <FaPizzaSlice />;
        if (/coffee|cafe/.test(catStr)) return <FaCoffee />;
        if (/restaurant|food|dining|lunch|dinner|breakfast/.test(catStr)) return <FaUtensils />;
        if (/gym|fitness/.test(catStr)) return <FaDumbbell />;
        if (/park/.test(catStr)) return <FaTree />;
        if (/pharmacy|drug/.test(catStr)) return <FaPills />;
        if (/hospital|clinic|medical/.test(catStr)) return <FaHospital />;
        if (/gas|fuel/.test(catStr)) return <FaGasPump />;
        if (/hotel|motel|lodging/.test(catStr)) return <FaHotel />;
        if (/library|campus|university|school/.test(catStr)) return <FaUniversity />;
        if (/shop|store|grocery|market|mall/.test(catStr)) return <FaShoppingBag />;
        return <FaStore />;
    })();

    const priceBadges = priceLevel
        ? Array.from({ length: Math.min(Number(priceLevel), 4) }, (_, i) => (
              <FaDollarSign key={i} style={{ color: '#1a73e8', fontSize: 14 }} />
          ))
        : null;

    return (
        <div style={{ padding: '12px 14px' }}>
            {/* Back button */}
            <div
                onClick={onBack}
                style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginBottom: 12,
                    color: '#1a73e8',
                    fontSize: 13,
                }}
            >
                <FaArrowLeft /> Back to places
            </div>

            {/* Name */}
            <div
                style={{ fontWeight: 700, fontSize: 16, color: '#222', display: 'flex', alignItems: 'center', gap: 8 }}
            >
                <span style={{ color: '#d64545', fontSize: 14 }}>{categoryIcon}</span>
                {item.name || '(unnamed place)'}
            </div>

            {/* Address */}
            <div style={{ color: '#777', fontSize: 13, marginTop: 4, marginBottom: 12 }}>
                <FaMapMarkerAlt style={{ color: '#d64545', fontSize: 12, marginRight: 4 }} />
                {item.formattedAddress || 'Address not available'}
            </div>

            {/* Rating + Price */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14 }}>
                {rating && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 18,
                            fontWeight: 700,
                            color: '#e8770e',
                        }}
                    >
                        <FaStar style={{ color: '#f5a623', fontSize: 16 }} /> {rating}
                    </div>
                )}
                {priceBadges && <div style={{ display: 'flex' }}>{priceBadges}</div>}
            </div>

            {/* Category badges */}
            {cats.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                    {cats.map((cat, i) => (
                        <Badge key={i} variant="light" style={{ fontSize: 12, color: '#555' }}>
                            {cat}
                        </Badge>
                    ))}
                </div>
            )}

            {/* Distance */}
            {item.distance != null && (
                <div style={{ fontSize: 13, color: '#777', marginBottom: 14 }}>Distance: {item.distance} m</div>
            )}

            {/* Source */}
            {item.source && (
                <div style={{ fontSize: 11, color: '#aaa', textTransform: 'capitalize', marginBottom: 14 }}>
                    Source: {item.source}
                </div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                    className="btn btn-primary btn-sm"
                    style={{ fontSize: 13, padding: '5px 14px' }}
                    onClick={() => onAddToCalendar?.(item)}
                >
                    <FaCalendarPlus className="mr-1" /> Add to Calendar
                </button>
                <a
                    className="btn btn-outline-secondary btn-sm"
                    style={{ fontSize: 13, padding: '5px 14px' }}
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <FaRoute className="mr-1" /> Open Route
                </a>
            </div>
        </div>
    );
}

export default PlaceDetail;
